import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { approvalNotice, askedAt, historyHref } from "@/lib/documents";
import type { Approval } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useApprovalDecision } from "./useDocument";

export { askedAt };

/** A request that was claimed but has no outcome stored: neither open nor settled. */
const unknownOutcome = (a: Approval) => approvalNotice(a)?.tone === "unknown";

/** On drafts and detail pages: the agent's open requests, each opening its dialog via `?freigabe=<id>`. */
export function ApprovalBanner({ approvals, number }: { approvals: Approval[]; number: string | null }) {
  const [, setSp] = useSearchParams();
  const shown = approvals.filter((a) => a.status === "pending" || unknownOutcome(a));
  if (shown.length === 0) return null;
  return (
    <section aria-label="Anfragen vom Agent" className="flex flex-col divide-y rounded-xl border bg-card">
      {shown.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <span aria-hidden className={cn("size-2 shrink-0 rounded-full", a.status === "pending" ? "bg-warning" : "bg-destructive")} />
          <span className="min-w-0 flex-1 text-sm">
            <span className="font-medium">{a.title}</span>
            <span className="text-muted-foreground"> · {askedAt(a.requestedAt)}</span>
            {a.status !== "pending" && (
              <span className="block text-[0.8125rem] text-muted-foreground">
                Ergebnis unbekannt –{" "}
                <Link to={historyHref(number)} className="text-foreground underline underline-offset-4">im Verlauf prüfen</Link>
              </span>
            )}
          </span>
          {a.status === "pending" && (
            <Button
              variant="outline"
              className="pointer-coarse:h-11"
              onClick={() => setSp((sp) => {
                const next = new URLSearchParams(sp);
                next.set("freigabe", String(a.id));
                return next;
              })}
            >
              Prüfen &amp; freigeben
            </Button>
          )}
        </div>
      ))}
    </section>
  );
}

/** Why a request opened from a link can no longer be decided (or how it ended). */
export function ApprovalNoticeLine({ approval, number }: { approval: Approval; number: string | null }) {
  const notice = approvalNotice(approval);
  if (!notice) return null;
  return (
    <p role="status" className={cn("text-sm", notice.tone === "failed" ? "text-destructive" : "text-muted-foreground")}>
      {notice.tone === "unknown" ? (
        <>Ergebnis unbekannt – <Link to={historyHref(number)} className="text-foreground underline underline-offset-4">im Verlauf prüfen</Link></>
      ) : notice.text}
    </p>
  );
}

/**
 * The approval mode's buttons: „Ablehnen“ asks for an optional reason, the primary approves. Once the request is no
 * longer open there is nothing to decide, only „Schließen“.
 */
export function ApprovalFooter({
  approval, primary, disabled, onApprove, onClose,
}: {
  approval: Approval;
  primary: string;
  disabled?: boolean;
  onApprove: () => void;
  onClose: () => void;
}) {
  const { reject } = useApprovalDecision(approval);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const open = approval.status === "pending";

  if (!open) {
    return (
      <DialogFooter>
        <Button variant="outline" className="pointer-coarse:h-11" onClick={onClose}>Schließen</Button>
      </DialogFooter>
    );
  }
  if (rejecting) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`reject-${approval.id}`}>Grund (optional, der Agent sieht ihn)</Label>
          <Textarea id={`reject-${approval.id}`} autoFocus rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        {reject.isError && <p role="alert" className="text-sm text-destructive">{reject.error.message}</p>}
        <DialogFooter>
          <Button variant="ghost" className="pointer-coarse:h-11" onClick={() => setRejecting(false)}>Zurück</Button>
          <Button
            variant="destructive"
            className="pointer-coarse:h-11"
            disabled={reject.isPending}
            onClick={() => reject.mutate(reason, { onSuccess: onClose })}
          >
            Ablehnen
          </Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <DialogFooter>
      <Button variant="outline" className="pointer-coarse:h-11" onClick={() => setRejecting(true)}>Ablehnen</Button>
      <Button className="pointer-coarse:h-11" disabled={disabled} onClick={onApprove}>{primary}</Button>
    </DialogFooter>
  );
}

/** Requests without their own dialog (deleting a draft, the Kleinunternehmer override): the title, then decide. */
export function SimpleApprovalDialog({ approval, number, onClose, onDone }: { approval: Approval; number: string | null; onClose: () => void; onDone: () => void }) {
  const { approve } = useApprovalDecision(approval);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{approval.action === "delete_draft" ? "Entwurf löschen?" : "Kleinunternehmergrenze überschreiten?"}</DialogTitle>
          <DialogDescription>{approval.title} · {askedAt(approval.requestedAt)}</DialogDescription>
        </DialogHeader>
        <ApprovalNoticeLine approval={approval} number={number} />
        {approve.isError && <p role="alert" className="text-sm text-destructive">{approve.error.message}</p>}
        <ApprovalFooter
          approval={approval}
          primary="Freigeben"
          disabled={approve.isPending}
          onApprove={() => approve.mutate({}, { onSuccess: onDone })}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
